import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LogIn, Settings, Image, BookOpen, Plus, Trash2, Pencil, Sparkles, Save, Eye, EyeOff, Globe, Send, MapPin, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("th_admin_token") || "");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    document.title = "Admin Dashboard | ToneHealing";
    if (token) verifyToken();
  }, []); // eslint-disable-line

  const verifyToken = async () => {
    try {
      await axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setIsLoggedIn(true);
    } catch {
      setToken(""); localStorage.removeItem("th_admin_token");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { username, password });
      setToken(r.data.token);
      localStorage.setItem("th_admin_token", r.data.token);
      setIsLoggedIn(true);
      toast.success("Welcome, Partner!");
    } catch {
      toast.error("Invalid credentials");
    }
    setLoginLoading(false);
  };

  const logout = () => {
    setToken(""); setIsLoggedIn(false);
    localStorage.removeItem("th_admin_token");
    toast.info("Logged out");
  };

  if (!isLoggedIn) return (
    <div className="min-h-screen flex items-center justify-center pt-16" data-testid="admin-login">
      <Card className="w-full max-w-sm bg-card border-border/50">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2"><LogIn className="w-5 h-5" /> Admin Login</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input placeholder="Username" value={username} onChange={e => setUsername(e.target.value)} data-testid="admin-username" />
            <Input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} data-testid="admin-password" />
            <Button type="submit" className="w-full" disabled={loginLoading} data-testid="admin-login-btn">
              {loginLoading ? "Logging in..." : "Login"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="pt-20 pb-16 max-w-6xl mx-auto px-4" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>ToneHealing Admin</h1>
        <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout">Logout</Button>
      </div>
      <Tabs defaultValue="settings" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5 bg-muted/30">
          <TabsTrigger value="settings" className="gap-1"><Settings className="w-3 h-3" /> Settings</TabsTrigger>
          <TabsTrigger value="banners" className="gap-1"><Image className="w-3 h-3" /> Banners</TabsTrigger>
          <TabsTrigger value="blog" className="gap-1"><BookOpen className="w-3 h-3" /> Blog</TabsTrigger>
          <TabsTrigger value="cities" className="gap-1"><MapPin className="w-3 h-3" /> Cities</TabsTrigger>
          <TabsTrigger value="indexnow" className="gap-1"><Globe className="w-3 h-3" /> IndexNow</TabsTrigger>
        </TabsList>
        <TabsContent value="settings"><SettingsTab token={token} /></TabsContent>
        <TabsContent value="banners"><BannersTab token={token} /></TabsContent>
        <TabsContent value="blog"><BlogTab token={token} /></TabsContent>
        <TabsContent value="cities"><CitiesTab token={token} /></TabsContent>
        <TabsContent value="indexnow"><IndexNowTab token={token} /></TabsContent>
      </Tabs>
    </div>
  );
}

function SettingsTab({ token }) {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/admin/settings`, { headers }).then(r => { setSettings(r.data.settings); setLoading(false); }).catch(() => setLoading(false));
  }, []); // eslint-disable-line

  const save = async () => {
    try {
      const r = await axios.put(`${API}/admin/settings`, settings, { headers });
      setSettings(r.data.settings);
      toast.success("Settings saved");
    } catch { toast.error("Failed to save"); }
  };

  if (loading) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <Card className="bg-card border-border/50" data-testid="settings-tab">
      <CardHeader><CardTitle>Site Settings</CardTitle></CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div><p className="font-medium text-sm">Google AdSense</p><p className="text-xs text-muted-foreground">Enable/disable AdSense ads</p></div>
          <Switch checked={settings.adsense_enabled || false} onCheckedChange={v => setSettings({...settings, adsense_enabled: v})} data-testid="adsense-toggle" />
        </div>
        <div>
          <label className="text-sm font-medium block mb-1">AdSense Publisher ID</label>
          <Input value={settings.adsense_publisher_id || ""} onChange={e => setSettings({...settings, adsense_publisher_id: e.target.value})} placeholder="ca-pub-XXXXXXXXXX" data-testid="adsense-id-input" />
        </div>
        <div className="flex items-center justify-between">
          <div><p className="font-medium text-sm">Bottom Banner</p><p className="text-xs text-muted-foreground">Show bottom banner on all pages</p></div>
          <Switch checked={settings.bottom_banner_enabled !== false} onCheckedChange={v => setSettings({...settings, bottom_banner_enabled: v})} data-testid="banner-toggle" />
        </div>
        <div>
          <label className="text-sm font-medium block mb-1">Site Name</label>
          <Input value={settings.site_name || ""} onChange={e => setSettings({...settings, site_name: e.target.value})} data-testid="site-name-input" />
        </div>
        <div>
          <label className="text-sm font-medium block mb-1">Contact Email</label>
          <Input value={settings.contact_email || ""} onChange={e => setSettings({...settings, contact_email: e.target.value})} data-testid="contact-email-input" />
        </div>
        <Button onClick={save} className="gap-2" data-testid="save-settings"><Save className="w-4 h-4" /> Save Settings</Button>
      </CardContent>
    </Card>
  );
}

function BannersTab({ token }) {
  const [banners, setBanners] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", image_url: "", click_url: "", html_content: "", alt_text: "", active: true });
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/banners`, { headers }); setBanners(r.data.banners); } catch {}
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/admin/banners/${editing}`, form, { headers });
        toast.success("Banner updated");
      } else {
        await axios.post(`${API}/admin/banners`, form, { headers });
        toast.success("Banner created");
      }
      setForm({ name: "", image_url: "", click_url: "", html_content: "", alt_text: "", active: true });
      setEditing(null);
      load();
    } catch { toast.error("Failed to save banner"); }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/admin/banners/${id}`, { headers }); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="banners-tab">
      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle>{editing ? "Edit Banner" : "Add Banner"}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Input placeholder="Banner name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="banner-name" />
          <Input placeholder="Image URL" value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} data-testid="banner-image-url" />
          <Input placeholder="Click URL" value={form.click_url} onChange={e => setForm({...form, click_url: e.target.value})} data-testid="banner-click-url" />
          <Textarea placeholder="HTML content (optional, alternative to image)" value={form.html_content} onChange={e => setForm({...form, html_content: e.target.value})} rows={3} data-testid="banner-html" />
          <Input placeholder="Alt text" value={form.alt_text} onChange={e => setForm({...form, alt_text: e.target.value})} data-testid="banner-alt" />
          <div className="flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={v => setForm({...form, active: v})} data-testid="banner-active" />
            <span className="text-sm">Active</span>
          </div>
          <div className="flex gap-2">
            <Button onClick={save} className="gap-2" data-testid="save-banner"><Save className="w-4 h-4" /> {editing ? "Update" : "Create"}</Button>
            {editing && <Button variant="ghost" onClick={() => { setEditing(null); setForm({ name: "", image_url: "", click_url: "", html_content: "", alt_text: "", active: true }); }}>Cancel</Button>}
          </div>
        </CardContent>
      </Card>
      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle>Existing Banners</CardTitle></CardHeader>
        <CardContent>
          {banners.length === 0 ? <p className="text-sm text-muted-foreground">No banners yet</p> : (
            <div className="space-y-3">
              {banners.map(b => (
                <div key={b.id} className="flex items-center justify-between p-4 rounded-xl bg-muted/20 border border-border/30">
                  <div>
                    <p className="font-medium text-sm">{b.name}</p>
                    <p className="text-xs text-muted-foreground">{b.active ? "Active" : "Inactive"} &middot; {b.image_url ? "Image" : "HTML"}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(b.id); setForm(b); }}><Pencil className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(b.id)}><Trash2 className="w-3 h-3 text-destructive" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function BlogTab({ token }) {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", slug: "", content: "", excerpt: "", category: "sound-therapy", published: false });
  const [generating, setGenerating] = useState(false);
  const [genTopic, setGenTopic] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/blog`, { headers }); setPosts(r.data.posts); } catch {}
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/admin/blog/${editing}`, form, { headers });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/admin/blog`, form, { headers });
        toast.success("Post created");
      }
      setForm({ title: "", slug: "", content: "", excerpt: "", category: "sound-therapy", published: false });
      setEditing(null);
      load();
    } catch { toast.error("Failed to save"); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/admin/blog/generate`, { topic: genTopic }, { headers });
      toast.success("Blog post generated! Review and publish when ready.");
      setGenTopic("");
      load();
    } catch (e) { toast.error("Generation failed: " + (e.response?.data?.detail || "Unknown error")); }
    setGenerating(false);
  };

  const togglePublish = async (post) => {
    try {
      await axios.put(`${API}/admin/blog/${post.id}`, { ...post, published: !post.published }, { headers });
      toast.success(post.published ? "Unpublished" : "Published");
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/admin/blog/${id}`, { headers }); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="blog-tab">
      {/* AI Generate */}
      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-accent" /> AI Blog Generator</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Input placeholder="Topic (leave empty for random)" value={genTopic} onChange={e => setGenTopic(e.target.value)} data-testid="gen-topic" />
          <Button onClick={generate} disabled={generating} className="gap-2" data-testid="generate-btn">
            <Sparkles className="w-4 h-4" /> {generating ? "Generating..." : "Generate Post"}
          </Button>
        </CardContent>
      </Card>

      {/* Manual Create/Edit */}
      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle>{editing ? "Edit Post" : "Create Post"}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Input placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="post-title" />
          <Input placeholder="Slug (auto-generated if empty)" value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} data-testid="post-slug" />
          <Textarea placeholder="Content (Markdown supported)" value={form.content} onChange={e => setForm({...form, content: e.target.value})} rows={10} data-testid="post-content" />
          <Input placeholder="Excerpt" value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} data-testid="post-excerpt" />
          <div className="flex items-center gap-2">
            <Switch checked={form.published} onCheckedChange={v => setForm({...form, published: v})} data-testid="post-published" />
            <span className="text-sm">Published</span>
          </div>
          <div className="flex gap-2">
            <Button onClick={save} className="gap-2" data-testid="save-post"><Save className="w-4 h-4" /> {editing ? "Update" : "Create"}</Button>
            {editing && <Button variant="ghost" onClick={() => { setEditing(null); setForm({ title: "", slug: "", content: "", excerpt: "", category: "sound-therapy", published: false }); }}>Cancel</Button>}
          </div>
        </CardContent>
      </Card>

      {/* Posts List */}
      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle>All Posts ({posts.length})</CardTitle></CardHeader>
        <CardContent>
          {posts.length === 0 ? <p className="text-sm text-muted-foreground">No posts yet. Generate one with AI or create manually.</p> : (
            <div className="space-y-3">
              {posts.map(p => (
                <div key={p.id} className="flex items-center justify-between p-4 rounded-xl bg-muted/20 border border-border/30">
                  <div className="flex-1 min-w-0 mr-4">
                    <p className="font-medium text-sm truncate">{p.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {p.published ? "Published" : "Draft"} &middot; {new Date(p.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => togglePublish(p)} title={p.published ? "Unpublish" : "Publish"}>
                      {p.published ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(p.id); setForm(p); }}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(p.id)}>
                      <Trash2 className="w-3 h-3 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}



function CitiesTab({ token }) {
  const [cities, setCities] = useState([]);
  const [total, setTotal] = useState(0);
  const [csvText, setCsvText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  // Import wave data
  const [waves, setWaves] = useState(null);
  useEffect(() => {
    import("@/data/cityWaves").then(mod => setWaves(mod));
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/cities`, { headers });
      setCities(r.data.cities);
      setTotal(r.data.total);
    } catch {}
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const uploadCities = async () => {
    setUploading(true);
    setResult(null);
    try {
      const lines = csvText.trim().split('\n');
      if (lines.length < 2) { toast.error("CSV needs a header row + data rows"); setUploading(false); return; }
      const headerRow = lines[0].toLowerCase().split(',').map(h => h.trim());
      const cityIdx = headerRow.indexOf('city');
      const stateIdx = headerRow.indexOf('state');
      const stateAbbrIdx = headerRow.indexOf('state_abbr');
      const popIdx = headerRow.indexOf('population');
      const riskIdx = headerRow.indexOf('risk_factors');

      if (cityIdx === -1 || stateIdx === -1) { toast.error("CSV must have 'city' and 'state' columns"); setUploading(false); return; }

      const citiesData = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map(c => c.trim());
        if (!cols[cityIdx]) continue;
        citiesData.push({
          city: cols[cityIdx],
          state: cols[stateIdx] || "",
          state_abbr: stateAbbrIdx >= 0 ? cols[stateAbbrIdx] : "",
          population: popIdx >= 0 ? parseInt(cols[popIdx]) || 0 : 0,
          risk_factors: riskIdx >= 0 ? cols[riskIdx] : "",
        });
      }

      const r = await axios.post(`${API}/admin/cities/upload`, { cities: citiesData }, { headers });
      setResult(r.data);
      toast.success(`Added ${r.data.added} cities! ${r.data.total_pages} total pages generated.`);
      setCsvText("");
      load();
    } catch (e) {
      toast.error("Upload failed: " + (e.response?.data?.detail || "Unknown error"));
    }
    setUploading(false);
  };

  const deleteCity = async (slug) => {
    try { await axios.delete(`${API}/admin/cities/${slug}`, { headers }); toast.success("City deleted"); load(); } catch { toast.error("Failed"); }
  };

  const deleteAll = async () => {
    if (!window.confirm("Delete ALL cities? This removes all city × condition pages.")) return;
    try { await axios.delete(`${API}/admin/cities`, { headers }); toast.success("All cities deleted"); load(); } catch { toast.error("Failed"); }
  };

  const totalPages = total * 56; // 56 conditions

  return (
    <div className="space-y-6" data-testid="cities-tab">
      {/* Stats */}
      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5 text-primary" /> Cities & Scale (Wave 2)</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="rounded-xl bg-muted/30 p-4 text-center">
              <p className="font-mono-freq text-3xl font-bold text-primary">{total}</p>
              <p className="text-xs text-muted-foreground mt-1">Cities</p>
            </div>
            <div className="rounded-xl bg-muted/30 p-4 text-center">
              <p className="font-mono-freq text-3xl font-bold text-primary">56</p>
              <p className="text-xs text-muted-foreground mt-1">Conditions</p>
            </div>
            <div className="rounded-xl bg-muted/30 p-4 text-center">
              <p className="font-mono-freq text-3xl font-bold text-accent">{totalPages.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground mt-1">City Pages</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">Upload cities via CSV to automatically generate <strong>{`city × condition`}</strong> pages. Each city creates 56 new pages (one per condition). All pages are automatically added to the sitemap and IndexNow.</p>
        </CardContent>
      </Card>

      {/* CSV Upload */}
      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="flex items-center gap-2"><Upload className="w-4 h-4" /> Upload Cities (CSV)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-xl bg-secondary/30 p-4">
            <p className="text-xs text-muted-foreground mb-2"><strong>CSV Format</strong> (required columns: city, state):</p>
            <code className="text-xs text-primary block">city,state,state_abbr,population,risk_factors</code>
            <code className="text-xs text-muted-foreground block">Houston,Texas,TX,2300000,Extreme heat and hurricane anxiety</code>
            <code className="text-xs text-muted-foreground block">Chicago,Illinois,IL,2700000,Harsh winters and seasonal depression</code>
            <code className="text-xs text-muted-foreground block">New York,New York,NY,8300000,High cost of living and urban noise</code>
          </div>
          <Textarea value={csvText} onChange={e => setCsvText(e.target.value)} placeholder="Paste your CSV data here..." rows={8} data-testid="csv-input" />
          <Button onClick={uploadCities} disabled={uploading || !csvText.trim()} className="gap-2 w-full" data-testid="upload-cities-btn">
            <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload & Create Pages"}
          </Button>
          {result && (
            <div className="rounded-xl bg-green-50 border border-green-200 p-4">
              <p className="text-sm font-semibold text-green-800">Upload Complete</p>
              <p className="text-xs text-green-700">Added: {result.added} cities | Skipped: {result.skipped} (duplicates)</p>
              <p className="text-xs text-green-700">Total cities: {result.total_cities} | Total new pages: {result.total_pages.toLocaleString()}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pre-Built Waves */}
      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle>Pre-Built City Waves</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">Ready-to-upload city data. Click a wave to load it, then hit "Upload & Create Pages" above.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {waves && [
              { id: 1, data: waves.WAVE_1, cities: 50, label: "Wave 1", desc: "Top 50 US metros", color: "border-primary/40 hover:bg-primary/5" },
              { id: 2, data: waves.WAVE_2, cities: 50, label: "Wave 2", desc: "Mid-size cities nationwide", color: "border-accent/40 hover:bg-accent/5" },
              { id: 3, data: waves.WAVE_3, cities: 50, label: "Wave 3", desc: "Expanding coverage", color: "border-orange-400/40 hover:bg-orange-50" },
            ].map(wave => (
              <button
                key={wave.id}
                onClick={() => { setCsvText(wave.data); toast.success(`Wave ${wave.id} loaded — ${wave.cities} cities. Click "Upload & Create Pages" to process.`); }}
                className={`text-left p-4 rounded-xl border-2 ${wave.color} transition-all`}
                data-testid={`load-wave-${wave.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-sm">{wave.label}</span>
                  <Badge variant="outline" className="text-xs">{wave.cities} cities</Badge>
                </div>
                <p className="text-xs text-muted-foreground">{wave.desc}</p>
                <p className="text-xs text-primary mt-2 font-medium">Click to load →</p>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">After loading a wave, click "Upload & Create Pages" above. Each city creates 56 pages (one per condition). Duplicates are automatically skipped.</p>
        </CardContent>
      </Card>

      {/* Cities List */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Cities ({total})</CardTitle>
            {total > 0 && <Button variant="destructive" size="sm" onClick={deleteAll} data-testid="delete-all-cities">Delete All</Button>}
          </div>
        </CardHeader>
        <CardContent>
          {cities.length === 0 ? (
            <p className="text-sm text-muted-foreground">No cities yet. Upload a CSV to get started.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {cities.map(c => (
                <div key={c.slug} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/30">
                  <div>
                    <p className="text-sm font-medium">{c.city}, {c.state_abbr}</p>
                    <p className="text-xs text-muted-foreground">Pop: {c.population?.toLocaleString() || 'N/A'} | 56 pages | /{c.slug}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => deleteCity(c.slug)}><Trash2 className="w-3 h-3 text-destructive" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function IndexNowTab({ token }) {
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/admin/indexnow/status`, { headers }).then(r => setStatus(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const submitAll = async () => {
    setSubmitting(true);
    setResults(null);
    try {
      const r = await axios.post(`${API}/admin/indexnow`, {}, { headers });
      setResults(r.data);
      toast.success(`Submitted ${r.data.total_urls} URLs to Bing, Yahoo, Yandex & DuckDuckGo!`);
    } catch (e) {
      toast.error("Submission failed: " + (e.response?.data?.detail || "Unknown error"));
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-6" data-testid="indexnow-tab">
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Globe className="w-5 h-5 text-primary" /> IndexNow — Search Engine Submission</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            IndexNow instantly notifies <strong>Bing, Yahoo, Yandex, and DuckDuckGo</strong> about your pages so they get indexed faster. 
            Google uses its own crawling system (submit your sitemap via <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Search Console</a>).
          </p>

          {status && (
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl bg-muted/30 p-4 text-center">
                <p className="font-mono-freq text-3xl font-bold text-primary">{status.total_pages}</p>
                <p className="text-xs text-muted-foreground mt-1">Total Pages to Submit</p>
              </div>
              <div className="rounded-xl bg-muted/30 p-4 text-center">
                <p className="font-mono-freq text-xs text-muted-foreground break-all">{status.indexnow_key}</p>
                <p className="text-xs text-muted-foreground mt-1">IndexNow Key</p>
              </div>
            </div>
          )}

          <div className="rounded-xl bg-secondary/30 p-4">
            <h4 className="text-sm font-semibold mb-2">What gets submitted:</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>Homepage</li>
              <li>All 53 condition therapy pages</li>
              <li>Blog listing + all published blog posts</li>
              <li>Legal pages (Privacy, Terms, Disclaimer)</li>
            </ul>
          </div>

          <Button onClick={submitAll} disabled={submitting} className="gap-2 w-full" size="lg" data-testid="indexnow-submit">
            <Send className="w-4 h-4" /> {submitting ? "Submitting to search engines..." : "Submit All Pages to Bing, Yahoo, Yandex & DuckDuckGo"}
          </Button>

          {results && (
            <div className="rounded-xl bg-green-50 border border-green-200 p-4">
              <p className="text-sm font-semibold text-green-800 mb-2">Submission Complete</p>
              <p className="text-xs text-green-700">Total URLs submitted: {results.total_urls}</p>
              {results.results.map((r, i) => (
                <p key={i} className="text-xs text-green-600 mt-1">
                  Batch {r.batch}: {r.urls} URLs — Status {r.status} {r.status === 200 || r.status === 202 ? "OK" : ""}
                </p>
              ))}
            </div>
          )}

          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
            <p className="text-xs text-amber-800">
              <strong>Note:</strong> For Google, you need to manually submit your sitemap at{" "}
              <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="underline">Google Search Console</a>.
              Your sitemap URL is: <code className="bg-amber-100 px-1 rounded">https://tonehealing.com/api/sitemap.xml</code>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
